import {
  ElementType,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from 'react';

import { ChipTypeMap } from '@mui/material/Chip';

import { useAppDispatch } from '../../../../store/hooks';
import { AsyncThunkFunctionModel } from '../../models/async-thunk-function.model';
import Autocomplete from '../ui/Autocomplete/Autocomplete';
import { DropdownProps, ItemLabelModel } from './models/dropdown-props.model';
import { getDropdownData } from './thunks/get-dropdown-data.thunk';

const Dropdown = <
  Entity,
  ItemLabelModelType,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent']
>(
  props: PropsWithChildren<
    DropdownProps<
      Entity,
      ItemLabelModelType,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent
    >
  >
): ReactElement => {
  const dispatch = useAppDispatch();

  const [options, setOptions] = useState<ItemLabelModel<ItemLabelModelType>[]>(
    []
  );

  const getData = (): void => {
    let result: ItemLabelModel<ItemLabelModelType>[] = [];
    const asyncThunkFunction: AsyncThunkFunctionModel<
      ItemLabelModel<ItemLabelModelType>[],
      string
    > = getDropdownData(props.dataMap);
    dispatch(asyncThunkFunction(props.endpoint))
      .unwrap()
      .then((itemsArr: ItemLabelModel<ItemLabelModelType>[]) => {
        result = itemsArr;
      })
      .catch(err => {
        console.warn(err);
      })
      .finally(() => {
        setOptions(result);
      });
  };

  useEffect(() => {
    getData();
    return () => setOptions([]);
  }, []);

  return (
    <Autocomplete
      sx={props.autoComplete.sx}
      value={props.autoComplete.value}
      onBlur={props.autoComplete.onBlur}
      onChange={props.autoComplete.onChange}
      inputValue={props.autoComplete.inputValue}
      onInputChange={props.autoComplete.onInputChange}
      id={props.autoComplete.id}
      options={options}
      getOptionLabel={props.autoComplete.getOptionLabel}
      isOptionEqualToValue={props.autoComplete.isOptionEqualToValue}
      renderInput={props.autoComplete.renderInput}
      className={props.autoComplete.className}
      multiple={props.autoComplete.multiple}
      filterSelectedOptions={props.autoComplete.filterSelectedOptions}
      disabled={props.autoComplete.disabled}
      classes={props.autoComplete.classes}
    />
  );
};

export default Dropdown;
